import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  TicketPlus,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Bell,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const MyDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('10days');

  const COLORS = ['#f59e0b', '#3b82f6', '#8b5cf6', '#10b981'];

  const getTooltipStyle = () => {
    return {
      backgroundColor: isDark ? '#1e293b' : '#f8fafc',
      border: `1px solid ${isDark ? '#475569' : '#e2e8f0'}`,
      borderRadius: '6px',
      color: isDark ? '#f1f5f9' : '#1e293b',
    };
  };

  const getDateRange = useCallback(() => {
    const now = new Date();
    let startDate = null;

    const dateRangeMap = {
      '24hr': 24 * 60 * 60 * 1000,
      '7days': 7 * 24 * 60 * 60 * 1000,
      '10days': 10 * 24 * 60 * 60 * 1000,
      '30days': 30 * 24 * 60 * 60 * 1000,
    };

    if (dateRangeMap[dateRange]) {
      startDate = new Date(now.getTime() - dateRangeMap[dateRange]);
    }

    return {
      startDate: startDate ? startDate.toISOString().split('T')[0] : null,
      endDate: now.toISOString().split('T')[0],
    };
  }, [dateRange]);

  // Helper to get status display value and styling
  const getStatusStyles = (status) => {
    const statusValue = status?.toString?.() || status || '';
    const statusMap = {
      'Open': { bg: 'bg-amber-100', text: 'text-amber-700', darkBg: 'dark:bg-amber-900/30', darkText: 'dark:text-amber-400' },
      'In Progress': { bg: 'bg-blue-100', text: 'text-blue-700', darkBg: 'dark:bg-blue-900/30', darkText: 'dark:text-blue-400' },
      'Resolved': { bg: 'bg-emerald-100', text: 'text-emerald-700', darkBg: 'dark:bg-emerald-900/30', darkText: 'dark:text-emerald-400' },
      'Closed': { bg: 'bg-slate-100', text: 'text-slate-700', darkBg: 'dark:bg-slate-900/30', darkText: 'dark:text-slate-300' },
    };
    return statusMap[statusValue] || statusMap['Closed'];
  };

  useEffect(() => {
    fetchUserDashboardData();
  }, [dateRange]);

  const fetchUserDashboardData = async () => {
    try {
      if (!loading) setRefreshing(true);
      if (loading) setLoading(true);
      const params = new URLSearchParams();
      const { startDate, endDate } = getDateRange();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      // Fetch all data in parallel
      const statsRes = api.get(`/dashboard/my-stats?${params}`);
      const recentRes = api.get('/tickets/my-recent?limit=5');
      const trendRes = api.get(`/dashboard/my-trend?${params}`);
      const perfRes = api.get(`/dashboard/my-performance?${params}`);
      const alertsRes = api.get('/dashboard/my-alerts');

      const results = await Promise.allSettled([statsRes, recentRes, trendRes, perfRes, alertsRes]);

      // Process results - each will have {status: 'fulfilled'|'rejected', value/reason}
      if (results[0].status === 'fulfilled') {
        const statsData = results[0].value.data;
        setStats(statsData);
      }
      if (results[1].status === 'fulfilled') {
        const data = results[1].value.data;
        setRecentTickets(Array.isArray(data) ? data : []);
      }
      if (results[2].status === 'fulfilled') {
        const trendData = results[2].value.data;
        setTrendData(Array.isArray(trendData) ? trendData : []);
      }
      if (results[3].status === 'fulfilled') {
        setPerformance(results[3].value.data);
      }
      if (results[4].status === 'fulfilled') {
        const data = results[4].value.data;
        setAlerts(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      // Error silently handled
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const statusData = stats ? [
    { name: 'Open', value: stats.open_tickets || 0, color: '#f59e0b' },
    { name: 'In Progress', value: stats.in_progress_tickets || 0, color: '#3b82f6' },
    { name: 'Resolved', value: stats.resolved_tickets || 0, color: '#8b5cf6' },
    { name: 'Closed', value: stats.closed_tickets || 0, color: '#10b981' },
  ].filter(item => item.value > 0) : [];

  const metricsCards = [
    {
      title: 'Open Tickets',
      value: stats?.open_tickets ?? 0,
      icon: AlertCircle,
      gradient: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
      darkIconColor: 'text-amber-400',
      darkTextColor: 'text-amber-400',
      today: stats?.open_today || 0,
    },
    {
      title: 'In Progress',
      value: stats?.in_progress_tickets ?? 0,
      icon: Clock,
      gradient: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      darkIconColor: 'text-blue-400',
      darkTextColor: 'text-blue-400',
      today: stats?.in_progress_today || 0,
    },
    {
      title: 'Resolved',
      value: stats?.resolved_tickets ?? 0,
      icon: CheckCircle2,
      gradient: 'from-emerald-500 to-green-500',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      darkIconColor: 'text-emerald-400',
      darkTextColor: 'text-emerald-400',
      today: stats?.resolved_today || 0,
    },
    {
      title: 'Closed',
      value: stats?.closed_tickets ?? 0,
      icon: XCircle,
      gradient: 'from-slate-500 to-slate-600',
      bgColor: 'bg-slate-50',
      iconColor: 'text-slate-600',
      darkIconColor: 'text-slate-300',
      darkTextColor: 'text-slate-300',
      today: stats?.closed_today || 0,
    },
  ];

  const StatCard = ({ card }) => (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 font-medium uppercase tracking-wider mb-1 sm:mb-2">
              {card.title}
            </p>
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
              {loading ? <Skeleton className="h-8 w-16" /> : card.value}
            </h3>
            {!loading && card.today !== undefined && (
              <p className="text-xs sm:text-sm mt-1 sm:mt-2 flex items-center gap-1 text-indigo-600 dark:text-indigo-400">
                <span className="font-medium">{card.today} today</span>
              </p>
            )}
          </div>
          <div className={`bg-gradient-to-br ${card.gradient} p-3 rounded-lg`}>
            <card.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="max-w-7xl mx-auto" data-testid="my-dashboard-page">
        {/* Header Section */}
        <div className="mb-1 sm:mb-2 md:mb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                Welcome back, {user?.full_name || 'User'} 👋
              </h1>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1 sm:mt-2">
                Here's what's happening with your support system today.
              </p>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <Button
                onClick={fetchUserDashboardData}
                disabled={refreshing}
                className="bg-slate-200 hover:bg-slate-300 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 font-medium shadow-sm transition-all py-1.5 sm:py-2 px-2.5 sm:px-3 text-xs sm:text-sm"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                onClick={() => navigate('/create-ticket')}
                className="bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white font-medium shadow-md shadow-indigo-500/25 hover:shadow-lg transition-all py-1.5 sm:py-2 px-3 sm:px-4 text-xs sm:text-sm"
              >
                <TicketPlus className="mr-2 h-4 w-4" />
                Create Ticket
              </Button>
            </div>
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-1 sm:mb-2 md:mb-3">
          {metricsCards.map((card, index) => (
            <StatCard key={index} card={card} />
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4 mb-1 sm:mb-2 md:mb-3">
          {/* Ticket Trend */}
          <Card className="lg:col-span-2 border-0 shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
            <CardHeader className="pb-1 sm:pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base sm:text-lg text-slate-900 dark:text-white">Ticket Trend</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">Last 10 days activity</CardDescription>
                </div>
                <TrendingUp className="h-5 w-5 text-indigo-600" />
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {loading || !trendData.length ? (
                <div className="h-64 flex items-center justify-center">
                  <Skeleton className="h-full w-full" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#475569' : '#e2e8f0'} />
                    <XAxis 
                      dataKey="date" 
                      stroke={isDark ? '#94a3b8' : '#64748b'} 
                      style={{ fontSize: '12px' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis stroke={isDark ? '#94a3b8' : '#64748b'} style={{ fontSize: '12px' }} />
                    <Tooltip contentStyle={getTooltipStyle()} />
                    <Legend />
                    <Line type="monotone" dataKey="created" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} />
                    <Line type="monotone" dataKey="resolved" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
            <CardHeader className="pb-1 sm:pb-2">
              <CardTitle className="text-base sm:text-lg text-slate-900 dark:text-white">Ticket Status</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Total{' '}
                <span className="font-bold text-slate-900 dark:text-white">
                  {stats?.total_tickets || 0}
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {loading || !statusData.length ? (
                <div className="h-64 flex items-center justify-center">
                  <Skeleton className="h-full w-full" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name} ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={getTooltipStyle()} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Performance & Recent Tickets */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
          {/* Performance Metrics */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
            <CardHeader className="pb-1 sm:pb-2">
              <CardTitle className="text-base sm:text-lg text-slate-900 dark:text-white">Performance</CardTitle>
              <CardDescription className="text-xs sm:text-sm">This week</CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">Avg. Response Time</p>
                    <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                      {performance?.avg_response_time || 'N/A'}
                    </p>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-500">↓ 12% vs last week</div>
                </div>
                <div className="border-t border-slate-200 dark:border-slate-700"></div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">Avg. Resolution Time</p>
                    <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                      {performance?.avg_resolution_time || 'N/A'}
                    </p>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-500">↓ 8% vs last week</div>
                </div>
                <div className="border-t border-slate-200 dark:border-slate-700"></div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">SLA Achievement</p>
                    <p className="text-sm sm:text-base font-bold text-emerald-600 dark:text-emerald-400">
                      {performance?.sla_achievement || 'N/A'}
                    </p>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-500">↑ 5% vs last week</div>
                </div>
                <div className="border-t border-slate-200 dark:border-slate-700"></div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">Customer Satisfaction</p>
                    <p className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                      {performance?.satisfaction_score || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Tickets */}
          <Card className="lg:col-span-2 border-0 shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
            <CardHeader className="pb-1 sm:pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base sm:text-lg text-slate-900 dark:text-white">Recent Tickets</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">Your latest activity</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/my-tickets')} className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 text-xs sm:text-sm">
                  View all
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {loading ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : recentTickets.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left py-2 px-2 font-medium text-slate-600 dark:text-slate-400">ID</th>
                        <th className="text-left py-2 px-2 font-medium text-slate-600 dark:text-slate-400">Subject</th>
                        <th className="text-left py-2 px-2 font-medium text-slate-600 dark:text-slate-400">Status</th>
                        <th className="text-left py-2 px-2 font-medium text-slate-600 dark:text-slate-400">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentTickets.map((ticket) => (
                        <tr
                          key={ticket.id}
                          className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/tickets/${ticket.id}`)}
                        >
                          <td className="py-2 px-2 text-slate-900 dark:text-slate-100 font-medium">{ticket.ticket_id}</td>
                          <td className="py-2 px-2 text-slate-700 dark:text-slate-300">{ticket.title || 'N/A'}</td>
                          <td className="py-2 px-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyles(ticket.status).bg} ${getStatusStyles(ticket.status).text} ${getStatusStyles(ticket.status).darkBg} ${getStatusStyles(ticket.status).darkText}`}>
                              {ticket.status}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-slate-500 dark:text-slate-400">{ticket.updated_at ? new Date(ticket.updated_at).toLocaleDateString() : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-600 dark:text-slate-400 text-sm">No tickets assigned to you yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <Card className="mt-1 sm:mt-2 md:mt-3 border-0 shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-slate-900/80 dark:border-slate-800">
            <CardHeader className="pb-1 sm:pb-2">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-red-600" />
                <CardTitle className="text-base sm:text-lg text-slate-900 dark:text-white">Alerts</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-3">
                {alerts.map((alert, index) => (
                  <div
                    key={index}
                    className="flex gap-3 p-3 sm:p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30"
                  >
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-900 dark:text-red-300">{alert.title || alert.message}</p>
                      {alert.description && (
                        <p className="text-xs text-red-700 dark:text-red-400 mt-1">{alert.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MyDashboard;
